import { useI18n } from 'vue-i18n'
import { i18n } from '@/i18n'

/**
 * Format integer cents as a localized currency string.
 *
 * @param amountCents - Amount in integer cents (e.g. 150000 = $1,500.00)
 * @param currency    - ISO 4217 currency code (e.g. 'USD', 'EUR')
 * @param locale      - BCP 47 locale string; defaults to the current i18n locale
 * @returns Formatted currency string (e.g. '$1,500.00', '1.500,00 €')
 *
 * @example
 * formatCurrency(150000, 'USD', 'en') // '$1,500.00'
 * formatCurrency(150000, 'EUR', 'de') // '1.500,00 €'
 * formatCurrency(0, 'USD', 'en')      // '$0.00'
 * formatCurrency(-500, 'USD', 'en')   // '-$5.00'
 */
export function formatCurrency(amountCents: number, currency: string, locale?: string): string {
    const resolvedLocale = locale ?? (i18n.global.locale.value as string)
    const amount = amountCents / 100

    try {
        return new Intl.NumberFormat(resolvedLocale, {
            style: 'currency',
            currency,
        }).format(amount)
    } catch {
        // Locale not supported — try with 'en' fallback
        try {
            return new Intl.NumberFormat('en', {
                style: 'currency',
                currency,
            }).format(amount)
        } catch {
            // Currency code is invalid — return plain number
            return new Intl.NumberFormat('en').format(amount)
        }
    }
}

/**
 * Format a number using locale-specific formatting.
 *
 * @param value  - The numeric value to format
 * @param locale - BCP 47 locale string; defaults to the current i18n locale
 * @param options - Intl.NumberFormatOptions overrides
 * @returns Formatted number string
 *
 * @example
 * formatNumber(1234567.89, 'en')                               // '1,234,567.89'
 * formatNumber(1234567.89, 'de')                               // '1.234.567,89'
 * formatNumber(1234567, 'en', { notation: 'compact' })         // '1.2M'
 */
export function formatNumber(
    value: number,
    locale?: string,
    options?: Intl.NumberFormatOptions,
): string {
    const resolvedLocale = locale ?? (i18n.global.locale.value as string)

    try {
        return new Intl.NumberFormat(resolvedLocale, options).format(value)
    } catch {
        return new Intl.NumberFormat('en', options).format(value)
    }
}

/**
 * Vue composable that wraps formatCurrency and formatNumber,
 * automatically reading the current i18n locale so components
 * stay reactive to locale changes.
 */
export function useCurrencyFormat() {
    const { locale } = useI18n()

    function format(amountCents: number, currency: string): string {
        return formatCurrency(amountCents, currency, locale.value)
    }

    function formatNum(value: number, options?: Intl.NumberFormatOptions): string {
        return formatNumber(value, locale.value, options)
    }

    return { format, formatNum }
}
